import type { Column, Row, RowSelectionState } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import type { Currency, Money } from '../../types/money';
import DropdownMenu from '../_radix-ui/dropdown-menu/DropdownMenu';
import type { DropdownMenuItem } from '../_radix-ui/dropdown-menu/DropdownMenu.types';
import Button from '../Button';
import Box from '../box/Box';
import styles from './Table.module.scss';

interface AggregateDropdownProps {
  alignRight: boolean;
  column: any;
  mandatory: boolean;
  numeric: boolean;
  rowSelection?: RowSelectionState;
  rows: any[];
  selectedFunction: string;
  onSelect: (value: any) => void;
  aggregateFunctions: { [key: string]: (rows: any[], column: any, rowSelection: any) => string };
}

const AggregateDropdown: React.FC<AggregateDropdownProps> = ({
  alignRight,
  column,
  mandatory,
  numeric,
  rowSelection,
  rows,
  selectedFunction,
  onSelect,
  aggregateFunctions,
}) => {
  const result = useMemo(() => {
    const aggregateFunction = aggregateFunctions[selectedFunction];
    return aggregateFunction(rows, column, rowSelection);
  }, [selectedFunction, rows, column, aggregateFunctions, rowSelection]);

  const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onSelect(e.target.value);
  };

  function uniqueCurrencies(
    rows: Row<any>[],
    column: Column<any>,
    rowSelection?: RowSelectionState
  ): Set<Currency> {
    const selectRows = rowSelection ? Object.keys(rowSelection).length : 0;
    const currencies = rows
      .filter(row => selectRows === 0 || row.getIsSelected())
      .map(row => {
        const cell = row.getVisibleCells().find(cell => cell.column.id === column.id);
        return (cell?.getValue() as Money)?.currency;
      });
    const uniqueCurrencies = new Set(currencies);
    return uniqueCurrencies;
  }

  const options = [
    ...(numeric
      ? mandatory
        ? [
            { value: 'sum', label: 'Sum' },
            { value: 'average', label: 'Average' },
            { value: 'median', label: 'Median' },
          ]
        : [
            { value: 'uniqueCount', label: 'Unique' },
            { value: 'sum', label: 'Sum' },
            { value: 'average', label: 'Average' },
            { value: 'median', label: 'Median' },
          ]
      : mandatory
        ? [{ value: 'uniqueCount', label: 'Unique' }]
        : [
            { value: 'uniqueCount', label: 'Unique' },
            { value: 'count', label: 'Count (Number)' },
            { value: 'countPercentage', label: 'Count (Percentage)' },
          ]),
  ];

  return (
    <Box.Row gap={8} justifyContent={alignRight ? 'flex-end' : 'flex-start'}>
      <DropdownMenu
        contentClassName={styles.aggregateDropdownContent}
        width={305}
        trigger={
          <Button priority="low" size="xsmall" data-testid="aggregate_datatable_footer">
            {result}
          </Button>
        }
        items={
          [
            ...[
              {
                type: 'radioGroup',
                label: 'Summary',
                currentValue: String(selectedFunction),
                onValueChange: (value: string) => {
                  handleSelect({
                    target: { value: value },
                  } as React.ChangeEvent<HTMLSelectElement>);
                },
                subOptions: options.map(option => ({
                  type: 'radioItem',
                  value: option.value,
                  label: option.label,
                  sidenote: aggregateFunctions[option.value]
                    ? aggregateFunctions[option.value](rows, column, rowSelection)
                    : '',
                })),
              },
              { type: 'separator' },
              {
                type: 'radioGroup',
                label: '',
                currentValue: String(selectedFunction),
                onValueChange: (value: string) => {
                  handleSelect({
                    target: { value: value },
                  } as React.ChangeEvent<HTMLSelectElement>);
                },
                subOptions: [
                  {
                    type: 'radioItem',
                    value: 'emptyLabel',
                    label: 'None',
                  },
                ],
              },
            ],
            ...(uniqueCurrencies(rows, column, rowSelection).size > 1
              ? [
                  { type: 'separator' },
                  {
                    type: 'message',
                    label: 'Sum, Average and Median for multiple currencies cannot be displayed',
                    variant: 'alert',
                  },
                ]
              : []),
          ] as DropdownMenuItem[]
        }
      />
    </Box.Row>
  );
};

export default AggregateDropdown;
